import update from 'immutability-helper';
import { AuthActionTypes, AuthActions, AuthState} from './actions';
import { AuthModals } from './types';
import { validateEmail, validatePassword, validateConfirmPassword, validateOTP } from './validation';

export const initialState: AuthState = {
  isModalOpen: false,
  modalStep: null,
  modalHistory: [],
  email: null,
  password: null,
  confirmPassword: null,
  messageCode: '',
  messageCodeError: null,
  isLoading: false,
  isUser: false,
  userName: null,
  userSurname: null,
  error: null,
};

export function authReducer(state = initialState, action: AuthActions): AuthState {
  switch (action.type) {
    case AuthActionTypes.TOGGLE_MODAL:
      return update(state, {
        isModalOpen: { $set: action.payload },
        ...(action.payload
          ? {}
          : {
              modalStep: { $set: null },
              modalHistory: { $set: [] },
              email: { $set: null },
              password: { $set: null },
              confirmPassword: { $set: null },
              messageCode: { $set: '' },
              messageCodeError: { $set: null },
              error: { $set: null },
              isLoading: { $set: false },
              isUser: { $set: false },
            }),
      });

    case AuthActionTypes.CHANGE_MODAL:
      return update(state, {
        modalHistory: state.modalStep ? { $push: [state.modalStep] } : { $set: [] },
        modalStep: { $set: action.payload },
        password: { $set: null },
        confirmPassword: { $set: null },
        messageCode: { $set: '' },
        messageCodeError: { $set: null },
        error: { $set: {} },
      });

    case AuthActionTypes.GO_BACK_MODAL:
      if (state.modalHistory.length === 0) return state;
      const previousStep = state.modalHistory[state.modalHistory.length - 1];
        return update(state, {
          modalStep: { $set: previousStep || null },
          modalHistory: { $set: state.modalHistory.slice(0, -1) },
          password: { $set: null },
          confirmPassword: { $set: null },
          messageCode: { $set: '' },
          messageCodeError: { $set: null },
          error: { $set: {} },
        });

    case AuthActionTypes.SET_EMAIL:
      return update(state, {
        email: { $set: action.payload },
        error: { $merge: { email: validateEmail(action.payload).error} },
      });

    case AuthActionTypes.SET_PASSWORD:
      const validation = validatePassword(action.payload);
      return update(state, {
        password: { $set: action.payload },
        error: { $merge: { password: validation.error } },
      });

    case AuthActionTypes.SET_CONFIRM_PASSWORD:
      return update(state, {
        confirmPassword: { $set: action.payload },
        error: { $merge: { confirmPassword: validateConfirmPassword(action.payload, state.password || '').error } },
      });

    case AuthActionTypes.CHANGE_MESSAGE_CODE:
      return update(state, {
        messageCode: { $set: action.payload },
        messageCodeError: { $set: validateOTP(action.payload).error },
      });

    case AuthActionTypes.SET_OTP_ERROR:
      return update(state, {
        messageCodeError: { $set: action.payload },
      });

    case AuthActionTypes.SIGN_UP:
      return update(state, {
        isLoading: { $set: true },
        error: {$set:{} },
      });

    case AuthActionTypes.SIGN_UP_SUCCESS:
      return update(state, {
        isLoading: { $set: false },
        modalStep: { $set: AuthModals.RegCode },
        modalHistory: state.modalStep ? { $push: [state.modalStep] } : { $set: state.modalHistory},
      });

    case AuthActionTypes.SIGN_UP_FAILED:
      return update(state, {
        isLoading: { $set: false },
        error: { general: { $set: action.error } },
      });

    case AuthActionTypes.CONFIRM_CODE:
      return update(state, {
        isLoading: { $set: true },
        error: {$set:{} },
      });

    case AuthActionTypes.CONFIRM_CODE_SUCCESS:
      return update(state, {
        isLoading: { $set: false },
        isUser: { $set: true },
        isModalOpen: { $set: false },
        modalStep: { $set: null },
        modalHistory: { $set: [] },
      });

    case AuthActionTypes.CONFIRM_CODE_FAILED:
      return update(state, {
        isLoading: { $set: false },
        error: { general: { $set: action.error } },
      });

    case AuthActionTypes.SIGN_IN:
      return update(state, {
        isLoading: { $set: true },
        error: {$set:{} },
      });

    case AuthActionTypes.SIGN_IN_SUCCESS:
      return update(state, {
        isLoading: { $set: false },
        isUser: { $set: true },
        isModalOpen: { $set: false },
        modalStep: { $set: null },
        modalHistory: { $set: [] },
      });

    case AuthActionTypes.SIGN_IN_FAILED:
      return update(state, {
        isLoading: { $set: false },
        error: { general: { $set: action.error } },
      });

    case AuthActionTypes.FORGOT_PASSWORD:
      return update(state, {
        isLoading: { $set: true },
        error: {$set:{} },
      });

    case AuthActionTypes.FORGOT_PASSWORD_SUCCESS:
      return update(state, {
        isLoading: { $set: false },
        modalStep: { $set: AuthModals.PinCode },
        modalHistory: state.modalStep ? { $push: [state.modalStep] } : { $set: state.modalHistory},
      });

    case AuthActionTypes.FORGOT_PASSWORD_FAILED: {
      const emailRelatedErrors = [
        'Username/client id combination not found.',
        'User does not exist',
        'Invalid email address',
      ];

      const isEmailError = emailRelatedErrors.some((msg) => action.error?.toLowerCase().includes(msg.toLowerCase()));

      return update(state, {
        isLoading: { $set: false },
        error: isEmailError ? { email: { $set: action.error } } : { general: { $set: action.error } },
      });
    }

    case AuthActionTypes.CONFIRM_PASSWORD:
      return update(state, {
        isLoading: { $set: true },
        error: {$set:{} },
      });

    case AuthActionTypes.CONFIRM_PASSWORD_SUCCESS:
      return update(state, {
        isLoading: { $set: false },
        isUser: { $set: true },
        isModalOpen: { $set: false },
        modalStep: { $set: null },
        modalHistory: { $set: [] },
      });

    case AuthActionTypes.CONFIRM_PASSWORD_FAILED:
      return update(state, {
        isLoading: { $set: false },
        error: { general: { $set: action.error } },
      });

    case AuthActionTypes.SIGN_OUT:
      return update(state, {
        isLoading: { $set: true },
        error: {$set:{} },
      });

    case AuthActionTypes.SIGN_OUT_SUCCESS:
      return update(state, {
        isLoading: { $set: false },
        isUser: { $set: false },
        email: { $set: null },
        password: { $set: null },
        confirmPassword: { $set: null },
        messageCode: { $set: '' },
        messageCodeError: { $set: null },
        error: { $set: {} },
      });

    case AuthActionTypes.SIGN_OUT_FAILED:
      return update(state, {
        isLoading: { $set: false },
        error: { general: { $set: action.error } },
      });

    case AuthActionTypes.GET_SESSION:
      return update(state, {
        isLoading: { $set: true },
        error: {$set:{} },
      });

    case AuthActionTypes.GET_SESSION_SUCCESS:
      return update(state, {
        isLoading: { $set: false },
        isUser: { $set: true },
        email: { $set: action.payload.email },
      });

    case AuthActionTypes.GET_SESSION_FAILED:
      return update(state, {
        isLoading: { $set: false },
        isUser: { $set: false },
        error: { general: { $set: action.error } },
      });

    default:
      return state;
  }
}
export default authReducer;